import CardType from "../../logic/payment/CardType";
import { uuid } from "vue-uuid";
import { DOWNLOAD_CODE_REGEX } from "@helpers/regexPatterns";

export default {
    mixins: [],
    data: () => ({
        deviceTransactionId: window?.transactionId || 'DEVICE_FINGERPRINTING_NOT_ENABLED'
    }),
    computed: {},
    watch: {},
    methods: {
        /**
         * The function "GetServices" retrieves items from a shopping cart and categorizes them as
         * either regular sales or hotel sales, and returns the data in a specific format.
         * @param Visitors - Visitors is a parameter that represents an array of visitors or guests who
         * will be participating in the activities or packages being purchased
         * @returns an object with a "Services" property that contains two properties: Activities and Packages
         */
        GetServices(Visitors) {
            let ActivitiesItems = [];
            let PackagesItems = [];
        
            /*
             * Ventas regulares / videos.
             */
            Object.values(this.getShoppingCart)
                .filter((item) => item.type == 1 || item.type == 3)
                .forEach((item) => {
                    ActivitiesItems.push({
                        RateKey: item.RateKey,
                        Amount: item.Amount,
                        Visitors: [Visitors],
                    });
                });
        
            /*
             * Reserva solo hotel:
             * Se vende como actividad regular.
             */
            Object.values(this.getShoppingCart)
                .filter(
                    (item) =>
                        (item.type === 2 || item.type === 0) &&
                        item.IsOnlyHotel
                )
                .forEach((item) => {
                    item.Parks.forEach((park) => {
                        ActivitiesItems.push({
                            RateKey: park.RateKey,
                            Amount: park.Amount,
                            Visitors: [Visitors],
                        });
                    });
                });
        
            /*
             * Paquetes de reserva / código de descarga.
             */
            Object.values(this.getShoppingCart)
                .filter(
                    (item) =>
                        (item.type === 2 || item.type === 0) &&
                        !item.IsOnlyHotel
                )
                .forEach((item) => {
                    const packageData = Array.isArray(item.sivexPayData)
                        ? item.sivexPayData
                        : [];
        
                    packageData.forEach((pack) => {
                        PackagesItems.push({
                            ...pack,
                            Visitors: [Visitors],
                        });
                    });
                });
        
            return {
                Services: {
                    Activities: ActivitiesItems,
                    Packages: PackagesItems,
                },
            };
        },

        /**
         * Validates the format accepted by ApiKoh for a download code.
         *
         * @param {*} downloadCode Candidate value.
         * @returns {boolean} True when the value is a valid download code.
         */
        isValidDownloadCode(downloadCode) {
            return DOWNLOAD_CODE_REGEX.test(String(downloadCode || "").trim());
        },
        /**
         * Gets the first valid download code found in a nested response object.
         * The backend and external providers may use either downloadCode or DownloadCode,
         * so both keys are supported. Validation messages returned inside error
         * nodes are ignored.
         *
         * @param {*} source Response object or array to inspect.
         * @returns {string|null} Download code found in the response.
         */
        getFirstDownloadCode(source) {
            if (!source || typeof source !== "object") {
                return null;
            }

            const keys = ["downloadCode", "DownloadCode"];

            for (const key of keys) {
                if (source[key] !== undefined && source[key] !== null && source[key] !== "") {
                    const downloadCode = String(source[key]).trim();

                    if (this.isValidDownloadCode(downloadCode)) {
                        return downloadCode;
                    }
                }
            }

            for (const value of Object.values(source)) {
                const downloadCode = this.getFirstDownloadCode(value);

                if (downloadCode) {
                    return downloadCode;
                }
            }

            return null;
        },
        /**
         * Stores the download code that must be displayed on the success payment page.
         * For existing download-code purchases the original code is preserved. For
         * video-only purchases, the code returned by Booking/ApiKoh is stored because
         * RecoverySale may not include it in the same node used by photo packages.
         *
         * @param {object} response Booking response containing the generated download code.
         */
        SetSuccessPaymentDownloadCode(response) {
            sessionStorage.removeItem("originalDownloadCode");

            const cartItems = Object.values(this.$store.state.shoppingCart || {});

            const type0Items = cartItems.filter((item) => Number(item.type) === 0);
            const lastType0Code = type0Items.length
                ? type0Items[type0Items.length - 1].code
                : null;

            if (lastType0Code) {
                sessionStorage.setItem("originalDownloadCode", lastType0Code);
                return;
            }

            const isOnlyVideoSale = cartItems.length > 0 && cartItems.every((item) => Number(item.type) === 3);

            if (!isOnlyVideoSale) {
                return;
            }

            const downloadCode = this.getFirstDownloadCode(response);

            if (downloadCode) {
                sessionStorage.setItem("originalDownloadCode", downloadCode);
            }
        },
        /**
         * This function determines the type of credit card being used based on the first six digits of
         * the card number and assigns the corresponding bank ID if applicable.
         * @returns Bin card details
         */
        async CardType() {
            try {
                // Retiramos los espacios sobre la tarjeta y la preparamos
                let card = this.removeSpace(this.PaymentMethods.card);
                // Seteamos el nuevo valor sin espacios
                this.PaymentMethods.card = card;

                const cardLenght = card.length;
                const binNumber = card.substring(0, 6);
                if (cardLenght < 6) {
                    return;
                }

                // CARD VISA | MASTERCARD | AMEX
                this.CardInfo = await CardType.get(binNumber);
                this.PaymentMethods.bank = null;
                //Le asignamos el idBank a la propiedad del select del Banco, si estamos fuera de Monedas extranjeras
                if (this.$store.state.Currency === "MXN") {
                    this.PaymentMethods.bank = this.CardInfo.IdBank;
                }
            } catch (error) {
            }
        },
        /**
         * The function `GetPayment` returns an object with payment information based on the selected
         * payment method and visitor information.
         * @param Visitors - It is a parameter that represents the buyer or visitor who is making the payment
         * @returns Returns an object.
         */
        async GetPayment(Visitors) {
            let Card = this.removeSpace(this.PaymentMethods.card);

            // PAYPAL
            if (this.PaymentMethod == 1) {
                return {
                    netCard: {
                        Buyer: Visitors,
                        Amount: this.$store.state.payment.Amount,
                        MSI: 1,
                        bankCharge: {
                            Id: 109,
                            Name: "OTROS",
                        },
                        cardType: {
                            Id: 9,
                            Name: "PayPal",
                        },
                        inauth: {
                            deviceTransactionId: this.deviceTransactionId,
                            returnURL: `${location}/${this.getLanguage}/carrito/netcard?method=PP`,
                            issuer: "",
                        },
                    },
                };
            }

            // OXXO
            if (this.PaymentMethod == 2 && this.CodeBankSafety == 15) {
                return {
                    netCard: {
                        Buyer: Visitors,
                        Amount: this.$store.state.payment.Amount,
                        MSI: 1,
                        bankCharge: {
                            Id: 109,
                            Name: "OTROS",
                        },
                        cardType: {
                            Id: 15,
                            Name: "OXXO",
                        },
                        inauth: {
                            returnURL: `${location}/${this.getLanguage}/carrito/details-payment/?details=POO`,
                            issuer: "",
                        },
                    },
                };
            }

            // SafetyPay(Walmart,  Super Farmacia Guadalajara,  Farmacias del Ahorro, Superama, etc..)
            if (this.PaymentMethod == 2 && this.CodeBankSafety != 15) {
                return {
                    netCard: {
                        Buyer: Visitors,
                        Amount: this.$store.state.payment.Amount,
                        MSI: 1,
                        bankCharge: {
                            Id: CodeBankSafety,
                            Name: "OTROS",
                        },
                        cardType: {
                            Id: 16,
                            Name: "SafetyPay",
                        },
                        inauth: {
                            returnURL: `${location}/${this.getLanguage}/carrito/details-payment/?details=POO`,
                            issuer: "",
                        },
                    },
                };
            }

            let cardsParameters = {
                CardNumber: Card,
                CVV: this.PaymentMethods.cvc,
                ExpireDate: {
                    Month: this.PaymentMethods.expireMonth,
                    Year: this.PaymentMethods.expireYear,
                },
                Buyer: Visitors,
                Amount: this.$store.state.payment.Amount,
                MSI: this.MSI,
                PaymentMethod: {
                    Id: parseInt(this.CardInfo.PaymentMethodCode) || 1,
                    Name: this.CardInfo.PaymentMethodName || "",
                },
                BankCharge: {
                    Id: parseInt(this.CardInfo.IdBank),
                    Name: this.CardInfo.Bank,
                },
            };

            // 1er Paso 3DS para enviar solo estos parametros
            if (this.isRequired3DS) {
                // cardsParameters.inauth.returnURL = this.$router.push({ name: 'challenge' })
                let baseUrl = window.location.origin;
                let url = this.$router.resolve({ name: "challenge" }).href;
                let inauth = { inauth: { returnURL: baseUrl + url } };

                cardsParameters = { ...inauth, ...cardsParameters };
            }

            // 2do Paso 3DS para enviar solo estos parametros
            if (this.requiredChallenge) {
                let cardsExtra = {
                    Challenge3DS: {
                        orderId: this.saleDetail.salesId.toString(),
                        merchant_Reference: this.saleDetail.invoiceId,
                        sessionId: this.challengeMD,
                    },
                };

                cardsParameters.CardNumber = this.saleDetail.cardNumber[0];

                cardsParameters = { ...cardsExtra, ...cardsParameters };
            }

            return {
                Cards: [cardsParameters],
            };
        },
        /**
         * This function handles the booking process for various payment methods and includes 3DS
         * validation and error handling.
         * @returns The function does not have a return statement. It may return undefined or a promise
         * depending on the execution path.
         * Type: 0 Card | 1 Paypal | 2 Convenience Store
         */
        async Booking() {
            try {
                if (this.currentStep === 2 || this.requiredChallenge) {
                    this.$store.dispatch("setLoading", true);
                    let formData = {};
                    const Visitors = {
                        FirstName: this.Buyer.FirstName,
                        LastName: this.Buyer.LastName,
                        FullName: this.Buyer.FullName,
                        Email: this.Buyer.Email,
                        Address: {
                            CountryId: this.Buyer.country.id,
                            StateId: this.Buyer.states.id,
                        },
                        Telephone: {
                            TelephoneClass: 1,
                            Number: this.Buyer.phone,
                        },
                        ClientType: 1,
                    };

                    let Services = this.GetServices(Visitors);
                    
                    let events = await this.$store.dispatch("getEventsInShoppingCart");
                    let shoppingCartData = events.find((el) => el.isEvent);

                    const eventDetails = shoppingCartData !== undefined
                        ? this.getShoppingCart[shoppingCartData.key].eventDetails
                        : null;

                    const agentDetails = eventDetails?.AgentDetails || {};
                    const defaultChannel = Number(
                        document.querySelector('meta[name="channel"]').getAttribute("content")
                    );
                    const eventChannel = Number(agentDetails?.Channel) || defaultChannel;

                    const hasAgentDetails = Object.keys(agentDetails).length > 0;
                    const shouldSendAgent = shoppingCartData !== undefined
                        ? !hasAgentDetails || agentDetails?.IsSend === true
                        : true;

                    let arg = {
                        Required3DS: false,
                        Type: this.PaymentMethod,
                        CodeBankSafety: this.CodeBankSafety,
                        ShoppingCart: this.getShoppingCart,
                        PromotionCode: "",
                        Header: {
                            Channel: shoppingCartData !== undefined
                                ? eventChannel
                                : defaultChannel,
                            ClientId: 0,
                            Currency: this.$store.state.Currency,
                            Language: this.getLanguage,
                            Country: "mex",
                            IP: window.ipAddress,
                            DeviceTransactionId: this.deviceTransactionId,
                            Browser: "SUF",
                        },
                        Payments: await this.GetPayment(Visitors),
                        Traveler: {
                            FirstName: this.Buyer.FirstName,
                            LastName: this.Buyer.LastName,
                            FullName: this.Buyer.FullName,
                            Email: this.Buyer.Email,
                            Address: {
                                CountryId: this.Buyer.country.id,
                                StateId: this.Buyer.states.id,
                            },
                            Telephone: {
                                TelephoneClass: 1,
                                Number: this.Buyer.phone,
                            },
                            ClientType: 1,
                        },
                        Analytics: {
                            ClientIdGa: "1667084626.1596297703",
                            UserIdGa:
                                "f709ecabfc4c6d6141412ee15f9aad96a21b9fb30250644ea5ea599be1d8d2b3",
                        },
                    };

                    if (shouldSendAgent) {
                        arg.agent = {
                            agentId: shoppingCartData !== undefined
                                ? agentDetails.AgentId || document.querySelector('meta[name="agentId"]').getAttribute("content")
                                : document.querySelector('meta[name="agentId"]').getAttribute("content"),
                            name: shoppingCartData !== undefined
                                ? agentDetails.Name || ""
                                : "",
                            boxOffice: shoppingCartData !== undefined
                                ? agentDetails.BoxOffice || document.querySelector('meta[name="boxOffice"]').getAttribute("content")
                                : document.querySelector('meta[name="boxOffice"]').getAttribute("content"),
                        };
                    }

                    /*3DS Paso 1 - Si la propiedad this.sessionId tiene valor, quiere decir que sera una primer peticion 
                    a Booking por que la tarjeta introducida requiere validacion 3DS*/
                    if (this.isRequired3DS) {
                        arg.Required3DS = true;
                        arg.Header.ddcsessionId = this.sessionId;
                    }

                    // 2do Paso 3DS para enviar solo estos parametros
                    if (this.requiredChallenge) {
                        arg.Header.tCookie = this.redirectDetails.cookie;
                        arg.reservation = {
                            salesId: this.saleDetail.salesId,
                            dsSalesId: this.saleDetail.invoiceId,
                        };
                    }

                    formData = { ...arg, ...Services };
                    formData.email = (this.Buyer?.Email || this.$store.state.email || "").trim();

                    let response = await this.$store.dispatch("Booking", formData);
                    this.$store.dispatch("setLoading", false);

                    // Verificamos si la venta no pudo guardarse en la DB de Foto e intentaremos de nuevo
                    if (response?.save?.apiKoh) {
                      sessionStorage.setItem("apiKoh", JSON.stringify(response.save.apiKoh));
                    }

                    // CARD VISA | MASTERCARD | AMEX
                    if (this.PaymentMethod == 0) {
                        // 3DS step 1 - Validacion para detectar que la tarjeta no es un error si no que requiere validacion 3DS
                        if (this.isRequired3DS && response?.statusPayment !== 2 && response?.redirectDetails) {
                            return response;
                        }

                        // si regresa 1 sin redirectDetails es que rechazó de plano la tarjeta
                        if (this.isRequired3DS && response?.statusPayment === 1 && !response?.redirectDetails) {
                            Vue.$toast.open({
                                message: this.$t("messages.error_sale_made"),
                                type: "error",
                            });
                            
                            return;
                        }

                        if (this.isRequired3DS) {
                            return response;
                        }

                        // Validacion por si el pago no fue aprovado arrojamos el error general
                        if (response?.saleStatus != 7) {
                            Vue.$toast.open({
                                message: this.$t("messages.error_sale_made"),
                                type: "error",
                            });
                            
                            return;
                        }
                        // Simulate an HTTP redirect:
                        // Verificamos si hay alguna venta existente para no mostrar el codigo de descarga
                        let existingSaleShoppingCart = this.$store.getters.existingSaleShoppingCart;
                        let query = { dsSalesId: response.invoiceId };
                        // Verificamos si hay alguna venta existente para enviar valor en la URL
                        if (existingSaleShoppingCart) {
                            query.existingSale = true;
                        }

                        // Guardamos el código que debe mostrarse en la pantalla de éxito.
                        this.SetSuccessPaymentDownloadCode(response);

                        /* Adding the class hidden to the element with the id
                                                        modalPaypalSuccess. */
                        document.getElementById("modalPaypalSuccess").classList.remove("hidden");
                        // Limpiamos el Carrito si la venta fue success

                        // Redirigimos al usuario al detalle de su venta
                        setTimeout(() => {
                            this.$router.push({ name: "success_payment", params: query });
                            this.$store.dispatch("clearShoppingCart");
                        }, 5000);
                        return;
                    }

                    // PAYPAL
                    if (this.PaymentMethod == 1) {
                        return response;
                    }

                    // OXXO
                    if (this.PaymentMethod == 2 && this.CodeBankSafety == 15) {
                        window.location.replace(response.acsURL);
                    }

                    // SafetyPay
                    if (this.PaymentMethod == 2 && this.CodeBankSafety != 15) {
                        // Guardamos el código que debe mostrarse en la pantalla de éxito.
                        this.SetSuccessPaymentDownloadCode(response);

                        // Simulate an HTTP redirect:
                        this.$router.push({
                            name: "success_payment",
                            params: { dsSalesId: response.ReservationNumber },
                        });
                        return;
                    }
                }

                this.currentStep++;
            } catch (error) {
                this.$store.dispatch("setLoading", false);
                Vue.$toast.open({
                    message: this.$t("Forms.error_sale"),
                    type: "error",
                });
            }
        },
    }
};